import type { SxProps, TextFieldProps } from '@mui/material';
import { TextField } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { Validator } from 'api/types';
import type { ChangeEvent, KeyboardEvent } from 'react';
import React, { useEffect, useRef, useMemo } from 'react';
import { getMaxCurrentWeekDate } from 'utils/index';
import ErrorMessage from './ErrorMessage';

export default function CustomTextfield({
  labelText,
  name,
  value,
  onChange,
  selectText,
  isRequired,
  placeholder,
  onKeyPress,
  type,
  disabled,
  maxLength,
  id,
  min,
  max,
  validator,
  className,
  validationString,
  dontDisplayErrorMsg,
  isTextarea,
  onBlur,
  InputProps,
  size,
  rows = 5,
  padding,
  autoFocus,
  prefix,
  multiple,
  fileType,
  focused,
  disableFutureDates,
  restrictToCurrentWeek,
  endAdornment,
  sx
}: {
  labelText?: string;
  name: string;
  value?: string;
  onChange?: ({
    target: { name, value }
  }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  selectText?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  onKeyPress?: (e: KeyboardEvent) => void;
  type?: 'text' | 'number' | 'date' | 'file' | 'time';
  disabled?: boolean;
  maxLength?: number;
  id?: string;
  min?: string;
  max?: string;
  validator?: Validator;
  className?: string;
  validationString?: string;
  dontDisplayErrorMsg?: boolean;
  isTextarea?: boolean;
  onBlur?: (event: { target: { name: string; value: string } }) => void;
  InputProps?: TextFieldProps['InputProps'];
  size?: 'small' | 'medium';
  padding?: number | string;
  autoFocus?: boolean;
  rows?: number;
  prefix?: React.ReactNode;
  fileType?: string;
  multiple?: boolean;
  focused?: boolean;
  disableFutureDates?: boolean;
  restrictToCurrentWeek?: boolean;
  endAdornment?: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  const isError = isRequired
    ? validator?.current?.message(name, value, validationString ? validationString : 'required')
    : false;

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleFocus = (e) => {
    if (selectText) {
      e.currentTarget.select();
    }
  };

  const getMaxDate = () => {
    if (type === 'date') {
      if (restrictToCurrentWeek) {
        return getMaxCurrentWeekDate();
      } else if (disableFutureDates) {
        const today = new Date();
        return today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      }
    }
    return max;
  };

  const maxAttribute = getMaxDate();

  const defaultSx = useMemo(
    () => ({
      '& .MuiInputBase-input': {
        padding: padding ?? undefined
      }
    }),
    [padding]
  );
  const mergedSx = useMemo(() => {
    if (!sx) return defaultSx;
    return [defaultSx, sx] as SxProps<Theme>;
  }, [defaultSx, sx]);

  return (
    <>
      <div style={{ position: 'relative' }}>
        {isTextarea ? (
          <TextField
            className={`${className || ''} `}
            error={isRequired && isError}
            placeholder={placeholder ?? ''}
            value={value}
            name={name}
            multiline
            rows={rows}
            fullWidth
            size={size || 'medium'}
            sx={mergedSx}
            onChange={onChange}
            disabled={disabled}
            label={labelText}
            inputProps={{
              maxLength
            }}
            {...(placeholder
              ? {
                  InputLabelProps: {
                    shrink: true
                  }
                }
              : {})}
            onBlur={onBlur}
            onKeyDown={onKeyPress}
          />
        ) : (
          <TextField
            className={` ${className || ''}`}
            type={type || 'text'}
            error={isRequired && isError}
            value={value}
            size={size || 'medium'}
            label={labelText}
            onChange={onChange}
            onKeyDown={onKeyPress}
            onBlur={onBlur}
            ref={inputRef}
            autoFocus={autoFocus}
            onFocus={handleFocus}
            fullWidth
            focused={focused}
            sx={mergedSx}
            placeholder={placeholder}
            disabled={disabled ? true : false}
            id={id ?? name}
            name={name}
            inputProps={{
              min,
              max: maxAttribute,
              maxLength,
              multiple,
              accept: fileType
            }}
            InputProps={{
              ...InputProps,
              startAdornment: prefix ? (
                <span style={{ marginRight: '8px', color: '#888' }}>{prefix}</span>
              ) : undefined,
              endAdornment: endAdornment ? (
                <span style={{ marginLeft: '8px', color: '#888' }}>{endAdornment}</span>
              ) : undefined
            }}
            {...(type === 'date' || type === 'time'
              ? {
                  InputLabelProps: {
                    shrink: true
                  }
                }
              : {})}
          />
        )}
        {isRequired && (
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '5px',
              color: 'red'
            }}>
            *
          </span>
        )}
      </div>
      {!dontDisplayErrorMsg && isRequired && isError ? (
        <ErrorMessage
          validator={validator}
          value={value}
          label={name}
          validationString={validationString || ''}
        />
      ) : (
        <p />
      )}
    </>
  );
}
